import React from 'react';
import { Tooltip, Icon, Checkbox, isItemSelected, Button } from '@jjj/components';
import { TextCell } from '../../../../../../components/tables/helperCells';
import CardsWrapper, { SelectAllWrapper, BulkAccreditationsWrapper, TalentLinkWrapper } from './poolList.style';
import { getFormattedDate } from '../../../../../../helpers/dateTime';
import { t, translateOptionLabel } from '../../../../../../i18n';
import { getActionColumn } from '../../../../../../components/tables/helperColumns';
import { getGroupedIcons } from '../../../../common/columns';
import SelectionTooltip from './components/selectionTooltip/selectionTooltip';
import { STATUS_KEY } from './poolList';
import { getLogo } from '../../../../../../helpers/image';

export function getColumns(component, isMyTeamTab, selectionData, isBulkTooltipVisible, total) {
  const selectedTalents = selectionData?.selectedTalents || [];
  const excludedTalents = selectionData?.excludedTalents || [];
  const bulkByField = selectionData?.bulkByField;
  const isBulkSelected = selectionData?.isBulkSelected || false;
  const bulkActionsEnabled = selectionData?.bulkActionsEnabled || false;
  const availableActions = selectionData?.availableActions || [];
  const availableStatuses = selectionData?.availableStatuses || [];
  const isAvailableActionsLoading = selectionData?.isAvailableActionsLoading || false;

  const isAnyItemSelected = selectedTalents.length || isBulkSelected || bulkByField;

  const { sortableColumns } = component.props.data;

  const getTooltip = () => {
    return (
      <SelectionTooltip
        loading={isAvailableActionsLoading}
        actions={availableActions}
        onActionClick={(event, action) => {
          event.stopPropagation();
          component.processBulkAction(action);
          component.handleBulkTooltipVisibleChange(false);
          component.props.clearSelection();
        }}
        onSelectBulkByField={(event, status) => {
          event.stopPropagation();
          component.onSelectByStatusClick({ field: STATUS_KEY, value: status });
        }}
        statuses={availableStatuses}
        selectByStatusEnabled={component.isSearchByStatusAvailable()}
      />
    );
  };

  const advancedPoolWorkersGridColumns = [
    bulkActionsEnabled && {
      title: (
        <SelectAllWrapper>
          <Checkbox
            disabled={!total}
            onChange={event => component.setSelected('all', event.target.checked)}
            checked={isBulkSelected}
            data-cy="job-booking-pools-bulk_select-checkbox"
          />
          <Tooltip
            trigger="click"
            classNames={{ root: 'filters-tooltip' }}
            placement="bottomRight"
            title={getTooltip}
            open={isBulkTooltipVisible}
            onOpenChange={component.handleBulkTooltipVisibleChange}
          >
            <Icon type="down" className="selectAllActionsIcon" />
          </Tooltip>
        </SelectAllWrapper>
      ),
      key: 'select_all',
      width: '45px',
      sorter: false,
      fixed: 'left',
      render: object => (
        <Checkbox
          className="flex-end"
          disabled={!object.current_organization}
          onChange={event => component.setSelected(object, event.target.checked)}
          defaultChecked={
            object.current_organization
              ? isItemSelected(object, isBulkSelected, selectedTalents, excludedTalents, bulkByField)
              : false
          }
          data-cy="job-booking-pools-select_all-checkbox"
        />
      ),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.worker_first_name_title'),
      key: 'first_name',
      width: bulkActionsEnabled ? 180 : '9%',
      sorter: sortableColumns.some(column => column === 'first_name'),
      render: object => (
        <TalentLinkWrapper>
          <Button
            onClick={() => {
              component.onTalentClick(object);
            }}
            type="link"
            data-cy="job-booking-pool-on-talent-first-name-click-button"
          >
            {TextCell(object.first_name)}
          </Button>
        </TalentLinkWrapper>
      ),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.worker_last_name_title'),
      key: 'last_name',
      width: bulkActionsEnabled ? 180 : '9%',
      sorter: sortableColumns.some(column => column === 'last_name'),
      render: object => (
        <TalentLinkWrapper>
          <Button
            onClick={() => {
              component.onTalentClick(object);
            }}
            type="link"
            data-cy="job-booking-pool-on-talent-last-name-click-button"
          >
            {TextCell(object.last_name)}
          </Button>
        </TalentLinkWrapper>
      ),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.tier_title'),
      key: 'tier',
      width: '9%',
      sorter: sortableColumns.some(column => column === 'tier'),
      render: object => TextCell(object.tier ? translateOptionLabel('level', object.tier?.id, object.tier?.name) : ''),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.accreditations_title'),
      key: 'accreditations',
      width: `${bulkActionsEnabled ? 12 : 9}%`,
      sorter: false,
      render: (object, url) => {
        const accreditationsCell = TextCell(getGroupedIcons(object?.accreditations, 'icon'), url);

        return <BulkAccreditationsWrapper>{accreditationsCell}</BulkAccreditationsWrapper>;
      },
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.ma_score_title'),
      key: 'ma_score',
      width: '9%',
      sorter: sortableColumns.some(column => column === 'ma_score'),
      render: object => TextCell(object.ma_score ? object.ma_score.toFixed(4) : object.ma_score),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.invitation_time_title'),
      key: 'invitation_time',
      width: `${bulkActionsEnabled ? 150 : '9%'}`,
      sorter: sortableColumns.some(column => column === 'invitation_time'),
      render: object =>
        object.isDateBold ? (
          <b>{TextCell(getFormattedDate({ date: object.invitation_time, formatKey: 'datetime_date_numeric' }))}</b>
        ) : (
          TextCell(getFormattedDate({ date: object.invitation_time, formatKey: 'datetime_date_numeric' }))
        ),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.hours_booked_weekly_title'),
      key: 'hours_booked_weekly',
      width: `${bulkActionsEnabled ? 11 : 9}%`,
      sorter: false,
      render: object => {
        const { overtime, hours_booked_weekly, potential_overtime } = object;

        return (
          <CardsWrapper>
            <div className="hours-booked-style">
              <p>{TextCell(hours_booked_weekly !== null ? hours_booked_weekly : t('dash.common.aliases.n/a'))}</p>
              {overtime && (
                <span>
                  <Tooltip title={t('dash.jobs.bookings.exceeds_weekly_hours_tooltip')} placement="bottom">
                    <Icon type="warning" theme="filled" className="hours-booked-warning-icon" />
                  </Tooltip>
                </span>
              )}
              {potential_overtime && !overtime && (
                <span>
                  <Tooltip
                    title={t('dash.jobs.bookings.assigning_will_exceed_weekly_hours_tooltip')}
                    placement="bottom"
                  >
                    <Icon type="warning" theme="filled" className="hours-booked-warning-icon" />
                  </Tooltip>
                </span>
              )}
            </div>
          </CardsWrapper>
        );
      },
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.hours_booked_daily_title'),
      key: 'hours_booked_daily',
      width: `${bulkActionsEnabled ? 11 : 9}%`,
      sorter: false,
      render: object => {
        const { overtime, hours_booked_daily, potential_overtime } = object;

        return (
          <CardsWrapper>
            <div className="hours-booked-style">
              <p>{TextCell(hours_booked_daily !== null ? hours_booked_daily : t('dash.common.aliases.n/a'))}</p>
              {overtime && (
                <span>
                  <Tooltip title={t('dash.jobs.bookings.exceeds_daily_hours_tooltip')} placement="bottom">
                    <Icon type="warning" theme="filled" className="hours-booked-warning-icon" />
                  </Tooltip>
                </span>
              )}
              {potential_overtime && !overtime && (
                <span>
                  <Tooltip
                    title={t('dash.jobs.bookings.assigning_will_exceed_weekly_hours_tooltip')}
                    placement="bottom"
                  >
                    <Icon type="warning" theme="filled" className="hours-booked-warning-icon" />
                  </Tooltip>
                </span>
              )}
            </div>
          </CardsWrapper>
        );
      },
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.worker_score_title'),
      key: 'worker_score',
      width: '9%',
      sorter: sortableColumns.some(column => column === 'worker_score'),
      render: object => (object.worker_score ? TextCell(object.worker_score) : ''),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.organization_title'),
      key: 'organization',
      width: '9%',
      sorter: sortableColumns.some(column => column === 'organization_id'),
      render: object => getLogo(object?.organization),
    },
    {
      title: t('dash.jobs.bookings.poolsColumns.inviter_name_title'),
      key: 'inviter_name',
      width: `${bulkActionsEnabled ? 7 : 9}%`,
      sorter: sortableColumns.some(column => column === 'inviter_name'),
      render: object => (object.inviter_name ? TextCell(object.inviter_name) : ''),
    },
    getActionColumn(
      object => component.popoverContent(object),
      {},
      bulkActionsEnabled ? { height: 30 } : {},
      isAnyItemSelected,
    ),
  ].filter(Boolean);

  if (isMyTeamTab || !component.props.isAdvancedPoolWorkersGrid) {
    return [
      {
        title: t('dash.jobs.bookings.poolsColumns.worker_first_name_title'),
        key: 'worker_first_name',
        width: '18%',
        sorter: false,
        render: object => TextCell(object.worker_first_name),
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.worker_last_name_title'),
        key: 'worker_last_name',
        width: '18%',
        sorter: false,
        render: object => TextCell(object.worker_last_name),
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.score_title'),
        key: 'score',
        width: '10%',
        sorter: false,
        render: object => TextCell(object.score ? object.score.toFixed(4) : object.score),
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.invitation_time_title'),
        key: 'invitation_time',
        width: '22%',
        sorter: false,
        render: object =>
          object.isDateBold ? (
            <b>{TextCell(getFormattedDate({ date: object.invitation_time, formatKey: 'datetime_date_numeric' }))}</b>
          ) : (
            TextCell(getFormattedDate({ date: object.invitation_time, formatKey: 'datetime_date_numeric' }))
          ),
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.hours_booked_title'),
        key: 'hours_booked',
        width: '11%',
        sorter: false,
        render: object => {
          const { overtime, hours_booked, potential_overtime } = object;

          return (
            <CardsWrapper>
              <div className="hours-booked-style">
                <p>{TextCell(hours_booked !== null ? hours_booked : t('dash.common.aliases.n/a'))}</p>
                {overtime && (
                  <span>
                    <Tooltip title={t('dash.jobs.bookings.exceeds_weekly_hours_tooltip')} placement="bottom">
                      <Icon type="warning" theme="filled" className="hours-booked-warning-icon" />
                    </Tooltip>
                  </span>
                )}
                {potential_overtime && !overtime && (
                  <span>
                    <Tooltip
                      title={t('dash.jobs.bookings.assigning_will_exceed_weekly_hours_tooltip')}
                      placement="bottom"
                    >
                      <Icon type="warning" theme="filled" className="hours-booked-warning-icon" />
                    </Tooltip>
                  </span>
                )}
              </div>
            </CardsWrapper>
          );
        },
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.status_title'),
        key: 'status',
        width: '11%',
        sorter: false,
        render: object => (object.status ? TextCell(object.status.name) : ''),
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.organization_title'),
        key: 'organization',
        width: '11%',
        sorter: false,
        render: object => getLogo(object?.organization),
      },
      {
        title: t('dash.jobs.bookings.poolsColumns.inviter_name_title'),
        key: 'inviter_name',
        width: '10%',
        sorter: false,
        render: object => (object.inviter_name ? TextCell(object.inviter_name) : ''),
      },
      getActionColumn(object => component.popoverContent(object)),
    ];
  }

  let columnIndex = component.props.isAdvancedPoolWorkersGrid ? 6 : 9;

  if (bulkActionsEnabled) {
    columnIndex += 1;
  }

  advancedPoolWorkersGridColumns.splice(columnIndex, 0, {
    title: t('dash.jobs.bookings.poolsColumns.status_title'),
    key: 'status',
    width: `${bulkActionsEnabled ? 13 : 9}%`,
    sorter: sortableColumns.some(column => column === 'status'),
    render: object => (object.status ? TextCell(object.status.name) : ''),
  });

  return advancedPoolWorkersGridColumns;
}
